import React, { useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { FooterTableSilicagel } from "./footerTableSilicagel";
import {
  CustomDataGrid,
  MTextField,
  ResponsiveFormContainer,
} from "@app/components";
import useAnalysis from "@app/pages/samples/utils/useAnalysis";

interface RowData {
  id: number;
  description: string;
  total_tramps: number;
  [key: string]: any;
}

const SilicaGelData = () => {
  const { currentPreparation } = useAnalysis();
  const form = useFormContext();
  const { setValue } = form;
  const [columns, setColumns] = useState([]);

  const totalWaterVolume = useWatch({
    name: "analyzedWaterVolume",
    control: form.control,
  }); // From "Datos Volúmenes"

  const analyzedWaterVolume = useWatch({
    name: "analyzedWaterVolume",
    control: form.control,
  }); // From "Seguimiento"

  const numberOfTraps = useWatch({
    name: "numberOfTraps",
    control: form.control,
  });

  const sampledAirVolume = useWatch({
    name: "sampledAirVolume",
    control: form.control,
  });

  const rows = useWatch({
    name: "rowsSilicaGelData",
    control: form.control,
    defaultValue: [],
  });

  useEffect(() => {
    const dynamicColumns: any = [
      {
        field: "description",
        headerName: "Trito en Aire - datos silicagel",
        editable: false,
        flex: 1,
      },
    ];

    for (let i = 1; i <= numberOfTraps; i++) {
      dynamicColumns.push({
        field: `trampa_${i}`,
        headerName: `Trampa ${i}`,
        editable: true,
        flex: 1,
      });
    }

    dynamicColumns.push({
      field: "total_tramps",
      headerName: "Totales",
      editable: false,
      flex: 1,
    });

    setColumns(dynamicColumns);
  }, [numberOfTraps]);

  useEffect(() => {
    const totalWaterRetained = rows.reduce((acc: any, row: any) => {
      if (row.id === 1 || row.id === 2) {
        for (let i = 1; i <= numberOfTraps; i++) {
          const valueBeforeExp = parseFloat(row[`trampa_${i}`]) || 0;
          const valueAfterExp =
            parseFloat(rows.find((r: any) => r.id === 2)?.[`trampa_${i}`]) || 0;
          acc += valueAfterExp - valueBeforeExp;
        }
      }
      return acc;
    }, 0);

    /* const sampledAirVolumeSilicagel =
      totalSampleAirVolume && analyzedWaterVolume && totalWaterRetained
        ? (totalSampleAirVolume * analyzedWaterVolume) / totalWaterRetained
        : 0;*/

    const sampledAirVolumeSilicagel =
      sampledAirVolume && totalWaterVolume && totalWaterRetained
        ? ((sampledAirVolume * totalWaterVolume) / totalWaterRetained).toFixed(
            2
          )
        : 0;

    form.setValue("sampledAirVolumeSilicagel", sampledAirVolumeSilicagel);
    if (sampledAirVolumeSilicagel) {
      form.setValue("analyzedAirVolume", sampledAirVolumeSilicagel);
    }
  }, [totalWaterVolume, analyzedWaterVolume, rows, numberOfTraps]); // eslint-disable-line react-hooks/exhaustive-deps

  const processRowUpdate = (newRow: RowData) => {
    if (newRow.id === 1 || newRow.id === 2) {
      let totalTramps = 0;
      let totalTrampsAfter = 0;
      const dataTrampsTritioAirArrayAfter: number[] = [];
      const dataTrampsTritioAirArrayBefore: number[] = [];

      for (let i = 1; i <= numberOfTraps; i++) {
        const value = newRow[`trampa_${i}`];
        const valueAsNumber = Number(value);
        const isValidNumber =
          !isNaN(valueAsNumber) && typeof valueAsNumber === "number";

        if (!isValidNumber) {
          newRow[`trampa_${i}`] = "";
          continue;
        }
        const volumenAire = parseFloat(value) || 0;

        //const volumenAire = parseFloat(newRow[`trampa_${i}`]) || 0;
        const valueAfterExp =
          parseFloat(rows.find((r: any) => r.id === 1)?.[`trampa_${i}`]) || 0;

        dataTrampsTritioAirArrayAfter.push(volumenAire);
        dataTrampsTritioAirArrayBefore.push(valueAfterExp);

        totalTramps += volumenAire;
        totalTrampsAfter += valueAfterExp;
      }
      newRow.total_tramps = totalTramps;

      setValue(
        "dataTrampsTritioAirArrayBefore",
        dataTrampsTritioAirArrayBefore
      );
      setValue("dataTrampsTritioAirArrayAfter", dataTrampsTritioAirArrayAfter);
      setValue("totalTrampsAfter", totalTrampsAfter);
      setValue("totalTrampsBefore", totalTramps);

      const updatedRows = rows.map((row: any) => {
        if (row.id === newRow.id) {
          return {
            ...newRow,
            total_tramps: totalTramps,
          };
        }
        return row;
      });
      setValue("rowsSilicaGelData", updatedRows);
    }
    return newRow;
  };

  return (
    <>
      <ResponsiveFormContainer columns={5} mt={2}>
        <MTextField
          name="numberOfTraps"
          label="Número de trampas"
          placeholder="Número de trampas"
          type="number"
          fullWidth
          inputProps={{ min: 0 }}
          validation={{
            required: "El número de trampas es requerido",
            min: {
              value: 0,
              message: "El número de trampas debe ser igual o mayor a cero",
            },
          }}
        />

        <MTextField
          key={JSON.stringify(form.getValues("sampledAirVolumeSilicagel"))}
          name="sampledAirVolumeSilicagel"
          label="Volumen de aire analizado (m³)"
          placeholder="Volumen de aire analizado (m³)"
          type="number"
          fullWidth
          disabled
        />
      </ResponsiveFormContainer>
      <CustomDataGrid
        rows={rows}
        columns={columns}
        search={false}
        processRowUpdate={processRowUpdate}
        disablePagination={true}
        key={`silica-gel-${currentPreparation?.index}-${currentPreparation?.id}`}
      />
      <FooterTableSilicagel columns={columns} rows={rows} />
    </>
  );
};

export default SilicaGelData;
